import React, { useState } from "react";
import { Button, Form, Row, Col, Spinner, InputGroup } from "react-bootstrap";
import type {
  OfflineEnrolmentFormState,
  OfflineEnrolmentFormLoading,
  OfflineEnrolmentFormMode,
  OfflinePlayerMode,
  RegisterBy,
} from "./useOfflineEnrolmentForm";
import type { PlayerProfile } from "../../../../api/academyEnrolments/enrolmentsService";

const GENDER_OPTIONS = ["Male", "Female", "Other"];

interface OfflinePlayerSectionProps {
  mode?: OfflineEnrolmentFormMode;
  /** True in edit mode when the enrolment has no attached player — re-enables the search/create UI */
  allowPlayerAttach?: boolean;
  state: OfflineEnrolmentFormState;
  loading: OfflineEnrolmentFormLoading;
  filteredPlayers: PlayerProfile[];
  onPlayerModeChange: (mode: OfflinePlayerMode) => void;
  onPhoneChange: (v: string) => void;
  onSearch: () => void;
  onCreateWebsiteUser: () => void;
  onShowAddProfile: (v: boolean) => void;
  onAddProfile: (registerBy: RegisterBy) => void;
  onRegisterByChange: (rb: RegisterBy) => void;
  onPlayerSelect: (p: PlayerProfile) => void;
  setNewParentName: (v: string) => void;
  setNewParentGender: (v: string) => void;
  setNewParentDob: (v: string) => void;
  setNewPlayerName: (v: string) => void;
  setNewPlayerGender: (v: string) => void;
  setNewPlayerDob: (v: string) => void;
  onDirectPlayerField: (field: "fullName" | "gender" | "dob", value: string) => void;
  onCreateDirectPlayer: () => void;
  onResetDirectPlayer: () => void;
}

const OfflinePlayerSection: React.FC<OfflinePlayerSectionProps> = ({
  mode = "add",
  allowPlayerAttach = false,
  state,
  loading,
  filteredPlayers,
  onPlayerModeChange,
  onPhoneChange,
  onSearch,
  onCreateWebsiteUser,
  onShowAddProfile,
  onAddProfile,
  onRegisterByChange,
  onPlayerSelect,
  setNewParentName,
  setNewParentGender,
  setNewParentDob,
  setNewPlayerName,
  setNewPlayerGender,
  setNewPlayerDob,
  onDirectPlayerField,
  onCreateDirectPlayer,
  onResetDirectPlayer,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") onSearch();
  };

  return (
    <div className="enrolment-form-section">
      <h6 className="enrolment-section-title">
        <span className="enrolment-section-number">1</span>
        Player
      </h6>

      {/* Sub-mode toggle — hidden in view mode */}
      {mode !== "view" && (
        <div className="d-flex gap-4 mb-3">
          <Form.Check
            type="radio"
            id="offline-player-mode-phone"
            label="Search by mobile number"
            checked={state.playerMode === "phone"}
            onChange={() => onPlayerModeChange("phone")}
          />
          <Form.Check
            type="radio"
            id="offline-player-mode-direct"
            label="Create player profile directly"
            checked={state.playerMode === "direct"}
            onChange={() => onPlayerModeChange("direct")}
          />
        </div>
      )}

      {/* ── Phone sub-mode ── */}
      {state.playerMode === "phone" && (
        <>
          {/* Phone search input — hidden in view mode */}
          {mode !== "view" && (
            <Row className="g-2">
              <Col xs={12} md={5}>
                <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
                  Mobile Number
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="tel"
                    placeholder="Enter 10-digit mobile number"
                    value={state.phoneNumber}
                    onChange={(e) =>
                      onPhoneChange(e.target.value.replace(/\D/g, "").slice(0, 10))
                    }
                    onKeyDown={handleKeyDown}
                    maxLength={10}
                  />
                  <Button
                    variant="primary"
                    onClick={onSearch}
                    disabled={state.phoneNumber.length < 10 || loading.phoneSearch}
                  >
                    {loading.phoneSearch ? (
                      <Spinner size="sm" animation="border" />
                    ) : (
                      "Search"
                    )}
                  </Button>
                </InputGroup>
              </Col>
            </Row>
          )}

          {/* Persisted selected profile - stays visible through phone searches */}
          {state.selectedPlayer && (
            <div className="mt-3">
              <Form.Label className="mb-1 text-muted d-block" style={{ fontSize: 12 }}>
                Added Profile
              </Form.Label>
              <div className="enrolment-player-list">
                <div className="enrolment-player-card selected" style={{ cursor: "default" }}>
                  <div className="enrolment-player-avatar">
                    {state.selectedPlayer.profilePic ? (
                      <img src={state.selectedPlayer.profilePic} alt={state.selectedPlayer.fullName} />
                    ) : (
                      <span>{state.selectedPlayer.fullName?.charAt(0)?.toUpperCase() ?? "?"}</span>
                    )}
                  </div>
                  <div className="enrolment-player-info">
                    <span className="enrolment-player-name">{state.selectedPlayer.fullName}</span>
                    {state.selectedPlayer.dob && (
                      <span className="enrolment-player-meta text-muted">
                        DOB: {state.selectedPlayer.dob}
                      </span>
                    )}
                    {state.selectedPlayer.gender && (
                      <span className="enrolment-player-meta text-muted">
                        {state.selectedPlayer.gender}
                      </span>
                    )}
                  </div>
                  <i className="ri-checkbox-circle-fill text-primary ms-auto" />
                </div>
              </div>
            </div>
          )}

          {/* Search results / add profile — hidden in view mode */}
          {mode !== "view" && (
            <>
              {/* User found */}
              {state.phoneSearched && state.searchedProfileData && !state.showAddProfileForm && (
                <div className="enrolment-profiles-found mt-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-success" style={{ fontSize: 13 }}>
                      <i className="ri-checkbox-circle-line me-1" />
                      User found —{" "}
                      <strong>{state.searchedProfileData.fullName}</strong>
                      {" "}(+91 {state.searchedProfileData.phoneNumber})
                    </span>
                    {(mode !== "edit" || allowPlayerAttach) && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => onShowAddProfile(true)}
                      >
                        + Add New Profile
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* User not found — edit mode (player already attached): show error + Add User button */}
              {mode === "edit" && !allowPlayerAttach && state.phoneSearched && state.isNewUser && (
                <div className="mt-3">
                  <div
                    className="d-flex align-items-center gap-2 mb-2 px-3 py-2 rounded"
                    style={{ background: "#fce4ec", border: "1px solid #f48fb1", fontSize: 13 }}
                  >
                    <i className="ri-user-unfollow-line text-danger" />
                    <span className="text-danger fw-medium">{state.phoneSearchError}</span>
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={loading.createUser}
                    onClick={onCreateWebsiteUser}
                  >
                    {loading.createUser ? (
                      <Spinner size="sm" animation="border" className="me-1" />
                    ) : null}
                    Add User
                  </Button>
                </div>
              )}

              {/* User not found — shown in add mode and when attaching a player in edit mode */}
              {(mode !== "edit" || allowPlayerAttach) && state.phoneSearched && state.isNewUser && !state.showAddProfileForm && (
                <div className="mt-3">
                  <div
                    className="d-flex align-items-center gap-2 mb-2 px-3 py-2 rounded"
                    style={{ background: "#fce4ec", border: "1px solid #f48fb1", fontSize: 13 }}
                  >
                    <i className="ri-user-unfollow-line text-danger" />
                    <span className="text-danger fw-medium">{state.phoneSearchError}</span>
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => onShowAddProfile(true)}
                  >
                    + Create New Profile
                  </Button>
                </div>
              )}

              {/* Add / Create profile sub-form — also shown when attaching a player in edit mode */}
              {(mode !== "edit" || allowPlayerAttach) && state.showAddProfileForm && (
                <AddProfileSubForm
                  globalRegisterBy={state.registerBy}
                  allowSelf={state.isNewUser}
                  state={state}
                  loading={loading}
                  setNewParentName={setNewParentName}
                  setNewParentGender={setNewParentGender}
                  setNewParentDob={setNewParentDob}
                  setNewPlayerName={setNewPlayerName}
                  setNewPlayerGender={setNewPlayerGender}
                  setNewPlayerDob={setNewPlayerDob}
                  onSubmit={onAddProfile}
                  onCancel={() => onShowAddProfile(false)}
                />
              )}

              {/* Register by + player list — shown in add mode and when attaching a player in edit mode */}
              {(mode !== "edit" || allowPlayerAttach) && state.phoneSearched && state.searchedProfileData && !state.showAddProfileForm && (
                <div className="mt-3">
                  <Form.Label className="mb-1 text-muted d-block" style={{ fontSize: 12 }}>
                    Registering As *
                  </Form.Label>
                  <div className="d-flex gap-3 mb-3">
                    {(["self", "parent"] as RegisterBy[]).map((rb) => (
                      <Form.Check
                        key={rb}
                        type="radio"
                        id={`offline-register-by-${rb}`}
                        label={rb === "self" ? "Self" : "Registering a child"}
                        checked={state.registerBy === rb}
                        onChange={() => onRegisterByChange(rb)}
                      />
                    ))}
                  </div>

                  {state.registerBy && (
                    <>
                      {(() => {
                        const displayPlayers = filteredPlayers.filter((p) => p._id !== state.selectedPlayer?._id);
                        return displayPlayers.length === 0 ? null : (
                          <div className="enrolment-player-list">
                            {displayPlayers.map((p) => (
                              <button
                                key={p._id}
                                type="button"
                                className={`enrolment-player-card${
                                  state.selectedPlayer?._id === p._id ? " selected" : ""
                                }`}
                                onClick={() => onPlayerSelect(p)}
                              >
                                <div className="enrolment-player-avatar">
                                  {p.profilePic ? (
                                    <img src={p.profilePic} alt={p.fullName} />
                                  ) : (
                                    <span>{p.fullName?.charAt(0)?.toUpperCase() ?? "?"}</span>
                                  )}
                                </div>
                                <div className="enrolment-player-info">
                                  <span className="enrolment-player-name">{p.fullName}</span>
                                  {p.dob && (
                                    <span className="enrolment-player-meta text-muted">
                                      DOB: {p.dob}
                                    </span>
                                  )}
                                  {p.gender && (
                                    <span className="enrolment-player-meta text-muted">
                                      {p.gender}
                                    </span>
                                  )}
                                </div>
                                {state.selectedPlayer?._id === p._id && (
                                  <i className="ri-checkbox-circle-fill text-primary ms-auto" />
                                )}
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── Direct sub-mode ── */}
      {mode !== "view" && state.playerMode === "direct" && (
        <>
          {/* Confirmed profile banner */}
          {state.directPlayerCreated ? (
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2 rounded"
              style={{ background: "#e8f5e9", border: "1px solid #a5d6a7", fontSize: 13 }}
            >
              <span className="text-success">
                <i className="ri-checkbox-circle-line me-1" />
                Player created —{" "}
                <strong>{state.directPlayerCreated.player.fullName}</strong>
              </span>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-muted"
                style={{ fontSize: 12 }}
                onClick={onResetDirectPlayer}
              >
                Reset
              </button>
            </div>
          ) : (
            <>
              <Row className="g-2 mb-2">
                <Col xs={12} md={4}>
                  <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
                    Player Name *
                  </Form.Label>
                  <Form.Control
                    placeholder="Full name"
                    value={state.directPlayer.fullName}
                    onChange={(e) => onDirectPlayerField("fullName", e.target.value)}
                  />
                </Col>
                <Col xs={6} md={2}>
                  <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
                    Gender
                  </Form.Label>
                  <Form.Select
                    value={state.directPlayer.gender}
                    onChange={(e) => onDirectPlayerField("gender", e.target.value)}
                  >
                    <option value="">Select</option>
                    {GENDER_OPTIONS.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
                    Date of Birth
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={state.directPlayer.dob}
                    onChange={(e) => onDirectPlayerField("dob", e.target.value)}
                  />
                </Col>
                <Col xs={12} md="auto" className="d-flex align-items-end">
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={
                      !state.directPlayer.fullName.trim() || loading.createDirectPlayer
                    }
                    onClick={onCreateDirectPlayer}
                    style={{ height: 38, whiteSpace: "nowrap" }}
                  >
                    {loading.createDirectPlayer ? (
                      <Spinner size="sm" animation="border" className="me-1" />
                    ) : null}
                    Create Profile
                  </Button>
                </Col>
              </Row>

              {state.directPlayerError && (
                <p className="text-danger mb-0 mt-1" style={{ fontSize: 13 }}>
                  <i className="ri-error-warning-line me-1" />
                  {state.directPlayerError}
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

// ── Add-Profile sub-form (reused from Excel flow) ─────────────────────────────

interface AddProfileSubFormProps {
  globalRegisterBy: RegisterBy | "";
  allowSelf: boolean;
  state: OfflineEnrolmentFormState;
  loading: OfflineEnrolmentFormLoading;
  setNewParentName: (v: string) => void;
  setNewParentGender: (v: string) => void;
  setNewParentDob: (v: string) => void;
  setNewPlayerName: (v: string) => void;
  setNewPlayerGender: (v: string) => void;
  setNewPlayerDob: (v: string) => void;
  onSubmit: (rb: RegisterBy) => void;
  onCancel: () => void;
}

const AddProfileSubForm: React.FC<AddProfileSubFormProps> = ({
  globalRegisterBy,
  allowSelf,
  state,
  loading,
  setNewParentName,
  setNewParentGender,
  setNewParentDob,
  setNewPlayerName,
  setNewPlayerGender,
  setNewPlayerDob,
  onSubmit,
  onCancel,
}) => {
  const [localRegisterBy, setLocalRegisterBy] = useState<RegisterBy | "">(
    !allowSelf ? "parent" : globalRegisterBy || ""
  );

  const isParent = localRegisterBy === "parent";

  const parentFieldsFilled =
    !!state.newParentName.trim() && !!state.newParentGender && !!state.newParentDob;

  const playerFieldsFilled =
    !isParent ||
    (!!state.newPlayerName.trim() && !!state.newPlayerGender && !!state.newPlayerDob);

  const canSubmit = !!localRegisterBy && parentFieldsFilled && playerFieldsFilled;

  return (
    <div className="enrolment-add-profile-form mt-3 p-3 border rounded">
      <p className="fw-medium mb-3" style={{ fontSize: 14 }}>
        {isParent ? "New Parent + Player Details" : "New Profile Details"}
      </p>

      {allowSelf ? (
        <>
          <Form.Label className="mb-1 text-muted d-block" style={{ fontSize: 12 }}>
            Registering As *
          </Form.Label>
          <div className="d-flex gap-3 mb-3">
            {(["self", "parent"] as RegisterBy[]).map((rb) => (
              <Form.Check
                key={rb}
                type="radio"
                id={`offline-add-profile-rb-${rb}`}
                label={rb === "self" ? "Self" : "Registering a child"}
                checked={localRegisterBy === rb}
                onChange={() => setLocalRegisterBy(rb)}
              />
            ))}
          </div>
        </>
      ) : (
        <p className="text-muted mb-3" style={{ fontSize: 12 }}>
          <i className="ri-information-line me-1" />
          A self profile already exists for this number. You can add a child profile below.
        </p>
      )}

      <Row className="g-2 mb-2">
        <Col xs={12} md={4}>
          <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
            {isParent ? "Parent Name" : "Full Name"} *
          </Form.Label>
          <Form.Control
            placeholder={isParent ? "Parent full name" : "Full name"}
            value={state.newParentName}
            onChange={(e) => setNewParentName(e.target.value)}
          />
        </Col>
        <Col xs={6} md={2}>
          <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
            Gender *
          </Form.Label>
          <Form.Select
            value={state.newParentGender}
            onChange={(e) => setNewParentGender(e.target.value)}
          >
            <option value="">Select</option>
            {GENDER_OPTIONS.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={6} md={3}>
          <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
            Date of Birth *
          </Form.Label>
          <Form.Control
            type="date"
            value={state.newParentDob}
            onChange={(e) => setNewParentDob(e.target.value)}
          />
        </Col>
      </Row>

      {isParent && (
        <>
          <hr className="my-2" />
          <p className="fw-medium mb-2" style={{ fontSize: 13 }}>
            Player (Child) Details
          </p>
          <Row className="g-2 mb-3">
            <Col xs={12} md={4}>
              <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
                Player Name *
              </Form.Label>
              <Form.Control
                placeholder="Player full name"
                value={state.newPlayerName}
                onChange={(e) => setNewPlayerName(e.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
                Gender *
              </Form.Label>
              <Form.Select
                value={state.newPlayerGender}
                onChange={(e) => setNewPlayerGender(e.target.value)}
              >
                <option value="">Select</option>
                {GENDER_OPTIONS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={3}>
              <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
                Date of Birth *
              </Form.Label>
              <Form.Control
                type="date"
                value={state.newPlayerDob}
                onChange={(e) => setNewPlayerDob(e.target.value)}
              />
            </Col>
          </Row>
        </>
      )}

      <div className="d-flex gap-2 mt-2">
        <Button
          variant="primary"
          size="sm"
          disabled={!canSubmit || loading.addProfile}
          onClick={() => localRegisterBy && onSubmit(localRegisterBy)}
        >
          {loading.addProfile ? (
            <Spinner size="sm" animation="border" />
          ) : (
            "Create Profile"
          )}
        </Button>
        <Button variant="outline-secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default OfflinePlayerSection;
